import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PNL_CUBE_MONTH_COLUMN, PNL_CUBE_SCENARIO_COLUMN } from '../starGridConstants';
import { QueryTooLargeError, deletePnlReport, getPnlReportConfig, queryPnlReport } from '../services/starGridService';
import { PnlPivotModel, PnlQueryResponse, PnlReportConfig } from '../starGridTypes';
import { buildPnlPivotModel } from '../utils/pnlPivot';

/** Info about a "result too large" 413 — feeds the UI's oversize alert. */
export interface OversizedResult {
  readonly estimatedRows: number;
  readonly rowLimit: number;
  readonly hint: string;
}

export interface UseStarGridPnlReportResult {
  /** The saved report definition (name, scenarios, drillable dims, filter-bar dims, ...). */
  readonly definition: PnlReportConfig | undefined;
  /** Wide pivot model for the grid, or undefined until the first query resolves. */
  readonly model: PnlPivotModel | undefined;
  /** Raw flat query response (kept for the generated-SQL / row-count surfaces). */
  readonly response: PnlQueryResponse | undefined;
  readonly loading: boolean;
  readonly error: string | undefined;
  /** Populated on HTTP 413 — the FE shows a "Result too large" banner + suggestion. */
  readonly oversized: OversizedResult | undefined;
  /** Currently-displayed row hierarchy dims (pivot-on-read state — starts coarse, grows on drill). */
  readonly groupBy: readonly string[];
  /** Currently-applied filter selections (dim → chosen values). */
  readonly filters: Readonly<Record<string, readonly string[]>>;
  /** Dimensions the report can drill into (drillable + filterable set minus current groupBy). */
  readonly drillableDimensions: string[];
  readonly deleting: boolean;
  readonly reload: () => void;
  /** Add a dimension to the row hierarchy and re-query (pivot-on-read drill). */
  readonly drillDown: (dimension: string) => void;
  /** Restore groupBy + filters to the report's default view. */
  readonly resetDrill: () => void;
  /** Apply a new filter selection and re-query. */
  readonly setFilters: (next: Record<string, readonly string[]>) => void;
  readonly remove: () => Promise<void>;
}

/**
 * Pivot-on-read state machine for a saved PnL report.
 *
 * The FE owns two pieces of state — ``groupBy`` (the row hierarchy) and ``filters`` —
 * and re-queries the MV whenever either changes. Initial values are seeded from the
 * report's ``default_group_by`` / ``default_filters``, so the FIRST render fires a
 * COARSE query (small result), not the full-grain MV scan. Drilling appends a dim to
 * ``groupBy``; Reset View restores the defaults.
 *
 * Ports xPT's ``usePnlViewerState.ts`` semantics onto StarRocks + MV (see memory
 * `stargrid-pnl-pivot-on-read-pattern`).
 */
export const useStarGridPnlReport = (reportId: number, onDeleted: () => void): UseStarGridPnlReportResult => {
  const [definition, setDefinition] = useState<PnlReportConfig | undefined>(undefined);
  /** Latest definition, kept in a ref so `runQuery` doesn't need it as a useCallback dep
   * (otherwise the load → setDefinition → runQuery-identity-change → load-identity-change
   *  → useEffect-refire loop makes every mount fire dozens of queries). */
  const definitionRef = useRef<PnlReportConfig | undefined>(undefined);
  const [response, setResponse] = useState<PnlQueryResponse | undefined>(undefined);
  const [model, setModel] = useState<PnlPivotModel | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [oversized, setOversized] = useState<OversizedResult | undefined>(undefined);
  const [deleting, setDeleting] = useState<boolean>(false);
  /** UI-state groupBy (pivot-on-read). Undefined until the definition seeds it. */
  const [groupBy, setGroupBy] = useState<readonly string[]>([]);
  const [filters, setFiltersState] = useState<Record<string, readonly string[]>>({});
  /** Once we've seeded state from the definition for this reportId, don't reseed on reload.
   *  Kept in a ref — including it in load's deps would re-invalidate load right after we
   *  seed and re-fire the whole load path (double /query per open). */
  const seededForRef = useRef<number | null>(null);
  /** Refs mirroring the latest groupBy/filters. `load` and `reload` read from these
   *  because they can't take groupBy/filters as useCallback deps (that would re-fire
   *  the load useEffect on every drill-down). Refs let the closures see current state
   *  without re-memoizing — fixes the "Refresh wipes the grid" bug. */
  const groupByRef = useRef<readonly string[]>([]);
  const filtersRef = useRef<Record<string, readonly string[]>>({});
  useEffect(() => {
    groupByRef.current = groupBy;
  }, [groupBy]);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const drillableDimensions = useMemo<string[]>(() => {
    if (!definition) return [];
    const pool = new Set<string>([...(definition.group_by_dimensions ?? []), ...(definition.filter_by_dimension ?? [])]);
    return Array.from(pool).filter((d) => !groupBy.includes(d));
  }, [definition, groupBy]);

  /** Fire a query at the current groupBy / filters. Reads the definition from a ref so
   *  this callback's identity is stable across renders — critical to keep `useEffect`
   *  from re-firing `load` after every `setDefinition`. */
  const runQuery = useCallback(
    async (nextGroupBy: readonly string[], nextFilters: Record<string, readonly string[]>, signal?: AbortSignal): Promise<void> => {
      const def = definitionRef.current;
      if (!def || nextGroupBy.length === 0) return;
      setLoading(true);
      setError(undefined);
      setOversized(undefined);
      try {
        const req = {
          group_by: [...nextGroupBy, PNL_CUBE_SCENARIO_COLUMN, PNL_CUBE_MONTH_COLUMN],
          filters: Object.fromEntries(Object.entries(nextFilters).map(([k, v]) => [k, [...v]])),
          scenarios: def.scenarios,
          variance_pairs: def.variance_pairs,
          measure: def.measure
        };
        const resp = await queryPnlReport(reportId, req, signal);
        if (signal?.aborted) return;
        setResponse(resp);
        setModel(buildPnlPivotModel(resp, def.scenarios, def.variance_pairs));
      } catch (caught) {
        if (signal?.aborted) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        if (caught instanceof QueryTooLargeError) {
          setOversized({ estimatedRows: caught.estimatedRows, rowLimit: caught.rowLimit, hint: caught.hint });
          setResponse(undefined);
          setModel(undefined);
        } else {
          setError(caught instanceof Error ? caught.message : 'Unknown error');
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [reportId]
  );

  /** Load the definition + seed pivot-on-read state + run the initial (default) query. */
  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(undefined);
      setOversized(undefined);
      setResponse(undefined);
      setModel(undefined);
      try {
        const def = await getPnlReportConfig(reportId, signal);
        if (signal?.aborted) return;
        setDefinition(def);
        definitionRef.current = def;
        if (def.status !== 'READY') return;
        // Seed groupBy/filters from the report's defaults ONLY the first time we open it
        // (or after an explicit resetDrill). Reloads read the CURRENT groupBy/filters
        // from refs — a raw state closure here would be stale (load's useCallback deps
        // deliberately exclude groupBy/filters).
        let seedGroupBy: readonly string[] = groupByRef.current;
        let seedFilters: Record<string, readonly string[]> = filtersRef.current;
        if (seededForRef.current !== reportId) {
          seedGroupBy = def.default_group_by && def.default_group_by.length > 0 ? def.default_group_by : def.group_by_dimensions.slice(0, 1);
          seedFilters = def.default_filters ?? {};
          setGroupBy(seedGroupBy);
          setFiltersState(seedFilters);
          seededForRef.current = reportId;
        }
        // Refresh path: if the user hasn't drilled or set any filters yet, refs are
        // still empty — fall back to the report's default seed so we never call
        // runQuery with an empty groupBy (which returns early and blanks the grid).
        if (seedGroupBy.length === 0) {
          seedGroupBy = def.default_group_by && def.default_group_by.length > 0 ? def.default_group_by : def.group_by_dimensions.slice(0, 1);
        }
        await runQuery(seedGroupBy, seedFilters, signal);
      } catch (caught) {
        if (signal?.aborted) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : 'Unknown error');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    // Intentionally exclude groupBy/filters (they change from callbacks that call runQuery
    // directly, not via load) and seededForRef (a ref — mutation doesn't re-render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportId, runQuery]
  );

  // Reset seed marker + local state when switching reports. The subsequent effect below
  // fires load which seeds fresh state from the new report's definition.
  useEffect(() => {
    if (seededForRef.current !== reportId) seededForRef.current = null;
    setGroupBy((prev) => (prev.length === 0 ? prev : []));
    setFiltersState((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [reportId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const drillDown = useCallback(
    (dimension: string): void => {
      setGroupBy((prev) => {
        if (prev.includes(dimension)) return prev;
        const next = [...prev, dimension];
        void runQuery(next, filters);
        return next;
      });
    },
    [runQuery, filters]
  );

  const resetDrill = useCallback((): void => {
    if (!definition) return;
    const seedGroupBy: readonly string[] =
      definition.default_group_by && definition.default_group_by.length > 0
        ? definition.default_group_by
        : definition.group_by_dimensions.slice(0, 1);
    const seedFilters = definition.default_filters ?? {};
    setGroupBy(seedGroupBy);
    setFiltersState(seedFilters);
    void runQuery(seedGroupBy, seedFilters);
  }, [definition, runQuery]);

  const setFilters = useCallback(
    (next: Record<string, readonly string[]>): void => {
      setFiltersState(next);
      void runQuery(groupBy, next);
    },
    [runQuery, groupBy]
  );

  const remove = useCallback(async () => {
    setDeleting(true);
    try {
      await deletePnlReport(reportId);
      onDeleted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unknown error');
      setDeleting(false);
    }
  }, [reportId, onDeleted]);

  return {
    definition,
    model,
    response,
    loading,
    error,
    oversized,
    groupBy,
    filters,
    drillableDimensions,
    deleting,
    reload: () => void load(),
    drillDown,
    resetDrill,
    setFilters,
    remove
  };
};
